import { getAuthCredentials } from "@/store/useAuthStore";
import { viewAsHeaderValue } from "@/lib/viewAs";

/**
 * TASK-ATT v1.3 — secured attachment download/preview helpers (W10/W12).
 *
 * NEVER the stock `/Attachment/file` or `?entryPoint=`: these custom routes set
 * `Content-Disposition: attachment` + `X-Content-Type-Options: nosniff` and
 * enforce parent-ACL.
 *
 * 🔴 Auth: this deployment has NO server session cookie — auth is the
 * `Espo-Authorization` token HEADER only (a bare `<a href>` → 401). So DOWNLOAD,
 * PREVIEW and OPEN-IN-NEW-TAB ALL go through `fetchAttachmentBlob()` (adds the
 * header) → a client-side object URL:
 *   - download  → `downloadAttachment()`        (programmatic <a download>)
 *   - open tab  → `openAttachmentInNewTab()`    (window.open(blob) → browser PDF/img viewer)
 *   - preview   → `fetchAttachmentBlob()` → <img src=objectUrl> (images only, NOT svg)
 * The blob is built from the authenticated attachment bytes (the server still sends
 * `Content-Disposition: attachment`); rendering it top-level in the user's own
 * browser viewer does NOT reintroduce an inline server vector (W10 intact).
 */

const API_BASE = "/api/v1";

/** Secured Task attachment streaming URL (W10) — anchor target. */
export function taskAttachmentDownloadUrl(
  taskId: string,
  attachmentId: string,
): string {
  return `${API_BASE}/Custom/frontend/tasks/${encodeURIComponent(
    taskId,
  )}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

/** Secured CCI attachment streaming URL (W10) — anchor target. */
export function cciAttachmentDownloadUrl(
  cciId: string,
  attachmentId: string,
): string {
  return `${API_BASE}/Custom/frontend/cci/${encodeURIComponent(
    cciId,
  )}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

/**
 * Builds the auth headers the SPA echoes on every request (mirror of
 * api/client.ts). Used for authed blob fetches that a bare <a> cannot carry.
 */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: "application/octet-stream",
    "X-Requested-With": "XMLHttpRequest",
  };
  const creds = getAuthCredentials();
  if (creds) {
    const enc = btoa(unescape(encodeURIComponent(`${creds.userName}:${creds.token}`)));
    headers["Espo-Authorization"] = enc;
    headers["Authorization"] = `Basic ${enc}`;
    headers["Espo-Authorization-By-Token"] = "true";
  }
  const viewAs = viewAsHeaderValue();
  if (viewAs) headers["X-Kormax-View-As"] = viewAs;
  return headers;
}

/**
 * Fetches a secured attachment as a Blob (for image preview via object URL).
 * Pass the URL from taskAttachmentDownloadUrl / cciAttachmentDownloadUrl.
 * Caller owns the object URL lifecycle (revoke on close).
 */
export async function fetchAttachmentBlob(url: string): Promise<Blob> {
  const r = await fetch(new URL(url, window.location.origin).toString(), {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!r.ok) {
    throw new Error(`Не вдалося завантажити файл (${r.status})`);
  }
  return r.blob();
}

/**
 * Authed download: fetches the secured URL as a blob (header auth) and triggers a
 * browser download with the original filename. A bare `<a href>` would 401 here
 * (no session cookie — token-header auth only).
 */
export async function downloadAttachment(url: string, filename: string): Promise<void> {
  const blob = await fetchAttachmentBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Opens the attachment in a new top-level browser tab (PDF/image preview via the
 * browser's native viewer). Fetches authed bytes → object URL → window.open. The
 * browser viewer is sandboxed by the UA and disables PDF JavaScript by default;
 * the file is server-side magic-byte-verified, so this is a safe preview path that
 * does NOT add an inline server vector (W10 intact). Returns false if the popup was
 * blocked (caller can fall back to download).
 */
export async function openAttachmentInNewTab(url: string): Promise<boolean> {
  const blob = await fetchAttachmentBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
  // Revoke after the new tab has had time to load (immediate revoke would blank it).
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return win != null;
}
